import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {getServerSendAsTargetLabel} from '../../../../models/api/serverHelpers';
import {
  ServerAuthenticationStrategiesEnum,
  ServerBearerAuthenticationConfig,
  ServerSendAsTargetsEnum,
} from '../../../../models/api/serverModel';
import {
  StyledParameterLineDiv,
  StyledParameterTitleLineDiv,
} from '../../../rules/ruleEditor/expressions/parameterLine';

/*
 * Constants.
 */

const ServerSendAsTargetsEnumValues = collectEnumValues(ServerSendAsTargetsEnum);

/*
 * Props.
 */

interface ServerAuthenticationBearerInputsProps {
  sendAs: ServerSendAsTargetsEnum;
  onUpdate: HandlerOf<ServerBearerAuthenticationConfig>;
  isReadOnly: boolean;
}

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0px;
  margin-bottom: 20px;
`;

/*
 * Component.
 */

export const ServerAuthenticationBearerInputs: FC<ServerAuthenticationBearerInputsProps> = (props) => {
  const {sendAs, onUpdate, isReadOnly} = props;

  const onUpdateSendAs = useCallback(
    (newSendAs: ServerSendAsTargetsEnum) => {
      onUpdate({
        strategy: ServerAuthenticationStrategiesEnum.BEARER,
        sendAs: newSendAs,
      });
    },
    [onUpdate],
  );

  return (
    <StyledParameterLineDiv>
      <StyledParameterTitleLineDiv>
        <Trans id="server-editor-send-as">Send as</Trans>
      </StyledParameterTitleLineDiv>
      <StyledDropdownBoxCoordinator
        size={VisualSizesEnum.MEDIUM}
        value={renderValue(sendAs)}
        renderDropdown={(popoverProps) => renderDropdown(popoverProps, sendAs, onUpdateSendAs)}
        isDisabled={isReadOnly}
      />
    </StyledParameterLineDiv>
  );
};

/*
 * Helpers.
 */

function renderValue(sendAs: ServerSendAsTargetsEnum) {
  return getServerSendAsTargetLabel(sendAs);
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  selectedSendAs: ServerSendAsTargetsEnum,
  onSelect: HandlerOf<ServerSendAsTargetsEnum>,
) {
  return (
    <SimpleDropdown {...popoverProps}>
      {ServerSendAsTargetsEnumValues.map((sendAsTarget) =>
        renderDropdownItem(sendAsTarget, sendAsTarget === selectedSendAs, onSelect),
      )}
    </SimpleDropdown>
  );
}

function renderDropdownItem(
  sendAs: ServerSendAsTargetsEnum,
  isChecked: boolean,
  onSelect: HandlerOf<ServerSendAsTargetsEnum>,
) {
  return (
    <DropdownItem key={sendAs} isChecked={isChecked} onSelect={() => onSelect(sendAs)} shouldReserveRightIcon>
      <DropdownItemContent isChecked={isChecked}>{getServerSendAsTargetLabel(sendAs)}</DropdownItemContent>
    </DropdownItem>
  );
}
